import React from 'react';
import { showsTimeCriticalChrome } from '../constants/statuses';

/**
 * The read-only "Expected Delivery: <date> [OVERDUE]" renderer.
 *
 * Extracted 2026-08-19 from `MyOrders.js` (its ONLY prior instance), so the
 * new expected-delivery editor in `OrderExpandedRow.js` can show the SAME
 * read-only fact for a non-manager viewer / a `delivered` order, rather than
 * hand-rolling a second copy — see `client/src/pages/CLAUDE.md`'s Shared
 * Modules rule and the CLAUDE.md `Code Unification Principle`.
 *
 * 🚨 **PRESERVE THE REJECTED/CANCELLED GUARD EXACTLY.** `showsTimeCriticalChrome`
 * is the shared predicate behind every indicator that only means something
 * while an order is still in flight (`constants/statuses.js`) — a `delivered`
 * order has arrived, a `cancelled` one is not coming, a `rejected` one is
 * bounced back to resubmit, not a live deadline, so an OVERDUE badge on any
 * of the three reads as a live problem that does not exist. This was a
 * CONTRACT CHANGE on 2026-08-17 (`MyOrders.overdueBadge.test.js`): the OLD
 * formula was `order.status !== 'delivered'`, which read a rejected/cancelled
 * order as overdue-eligible. Do not revert to a hand-rolled status check.
 *
 * Two independent gates, in order:
 *   1. The OUTER gate on whether to render anything at all — this component
 *      shows nothing unless `expected_delivery_date` is set AND the status is
 *      one where a delivery is still being tracked (`ordered` /
 *      `partially_delivered` / `delivered`). This already excludes
 *      `rejected`/`cancelled` unconditionally, before `isOverdue` is ever
 *      computed — a second, independent line of defence beside (2).
 *   2. `isOverdue`, gated on `showsTimeCriticalChrome(order)` as well as the
 *      date comparison — the rejected/cancelled guard this component exists
 *      to preserve.
 *
 * Local-midnight date parsing throughout (`+ 'T00:00:00'`, `setHours(0,0,0,0)`)
 * — never `toISOString()` — matching every other date comparison in this
 * codebase (`server/tests/regressions/no-utc-date-drift.test.js` scans for
 * the UTC-slice shape and fails on it).
 *
 * 🎨 **THE BADGE OWNS ITS OWN TEXT COLOUR, ON THE SPANS — fixed 2026-09-12.**
 * It carried NO colour of its own until then and simply inherited whatever
 * its parent gave it. That worked on `MyOrders.js` by luck — its badge sat
 * inside the (since retired) card row's secondary-info wrapper, which already
 * set `text-sm text-gray-600 dark:text-gray-400` — and failed on the expanded row,
 * whose metadata grid in `OrderExpandedRow.js` sets `text-sm` and NO colour,
 * so the date rendered black on the dark background. Owner, 2026-09-12,
 * Department Orders: *"Expected Delivery: 14 Aug 2026 is black in dark mode so
 * its hard to see"*. 🔑 **A shared component whose legibility depends on where
 * it is mounted is one new call site away from this bug every time** — the
 * colour belongs here, not in the callers.
 * `text-gray-700 dark:text-gray-300` MATCHES the sibling control a manager sees
 * on the same row, `InlineExpectedDeliveryEditor.js`'s label, so the editable
 * and read-only forms of the same fact read identically.
 * ⚠️ **On the SPANS, deliberately, not on the `<p>`** — `className` is the
 * caller's slot and must keep controlling SPACING only; neither call site
 * passes a colour, and a colour on the `<p>` would compete with a caller's in
 * the same class attribute, where the winner is decided by stylesheet source
 * order rather than by the order the classes are written in.
 * 🔴 The OVERDUE variant gained `dark:text-red-400` (it had `text-red-600`
 * only); the OVERDUE PILL beside it is left alone on purpose — `bg-red-100
 * text-red-700` paints its OWN ground, so it does not inherit the panel and
 * reads the same in both themes.
 * 🚨 Pinned by `ExpectedDeliveryBadge.test.js` — which jsdom can only do at the
 * CLASS level: it applies no stylesheet, resolves no colour and has no notion
 * of a dark theme, so those tests catch a MISSING variant, never a wrong shade.
 *
 * @param {Object} props
 * @param {Object} props.order — must carry `expected_delivery_date` and
 *   `status` (and whatever `showsTimeCriticalChrome` itself reads).
 * @param {string} [props.testId] — `data-testid` on the outer `<p>`.
 * @param {string} [props.className] — outer `<p>` class, defaulting to the
 *   spacing MyOrders.js always used (`mt-0.5`); the new expanded-row site
 *   passes its own to match its metadata grid.
 */
const EXPECTED_DELIVERY_VISIBLE_STATUSES = ['ordered', 'partially_delivered', 'delivered'];

export default function ExpectedDeliveryBadge({ order, testId, className = 'mt-0.5' }) {
  if (!order.expected_delivery_date || !EXPECTED_DELIVERY_VISIBLE_STATUSES.includes(order.status)) {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expDate = new Date(order.expected_delivery_date + 'T00:00:00');
  expDate.setHours(0, 0, 0, 0);
  // Same guard as the pre-extraction site — see the docblock above.
  const isOverdue = expDate < today && showsTimeCriticalChrome(order);
  const formatted = expDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <p data-testid={testId} className={className}>
      <span className="font-medium text-gray-700 dark:text-gray-300">Expected Delivery:</span>{' '}
      {isOverdue
        ? (
          <span className="text-red-600 dark:text-red-400 font-semibold">
            {formatted} <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs font-bold">OVERDUE</span>
          </span>
        )
        : <span className="text-gray-700 dark:text-gray-300">{formatted}</span>}
    </p>
  );
}
